import { WORKFLOW_WAITING_STEP_TYPES } from "@sagot/constants";
import { exitSlotsOf } from "./exit-slots";
import type { Definition } from "./types";

/** Loops: a path that leads back to itself without ever waiting for the visitor would run forever (backend `_edges` and `_loops`). */

/**
 * Path -> the paths it can lead to within a single turn, without the visitor answering anything first: the exits of
 * every step before the first waiting step of the path (backend `_edges(without_waiting=True)`). The waiting step's
 * own exits are not part of it: they only happen after the wait.
 */
export function edgesWithoutWaiting(definition: Definition): Map<string, Set<string>> {
  const edges = new Map<string, Set<string>>();
  for (const [id, path] of Object.entries(definition.paths)) {
    const targets = new Set<string>();
    for (const step of path.steps) {
      if (WORKFLOW_WAITING_STEP_TYPES.has(step.type)) break;
      for (const slot of exitSlotsOf(step)) if (slot.target) targets.add(slot.target);
    }
    edges.set(id, targets);
  }
  return edges;
}

/** Whether `from` can reach `to` along `edges` (a path reaches itself only through a cycle). */
export function reaches(edges: ReadonlyMap<string, ReadonlySet<string>>, from: string, to: string): boolean {
  const seen = new Set<string>();
  const queue = [from];
  for (let id = queue.pop(); id !== undefined; id = queue.pop()) {
    if (id === to) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    queue.push(...(edges.get(id) ?? []));
  }
  return false;
}

const WHITE = 0;
const GREY = 1;
const BLACK = 2;

/** Paths that sit on a cycle (backend `_loops`: depth-first search with colours), sorted. */
export function loopingPaths(edges: ReadonlyMap<string, ReadonlySet<string>>): string[] {
  const colour = new Map<string, number>([...edges.keys()].map((id) => [id, WHITE]));
  const looping = new Set<string>();
  const childrenOf = (id: string): string[] => [...(edges.get(id) ?? [])].sort();
  for (const start of edges.keys()) {
    if (colour.get(start) !== WHITE) continue;
    const stack: { node: string; children: string[] }[] = [{ node: start, children: childrenOf(start) }];
    colour.set(start, GREY);
    for (let top = stack.at(-1); top; top = stack.at(-1)) {
      const child = top.children.shift();
      if (child === undefined) {
        colour.set(top.node, BLACK);
        stack.pop();
      } else if (colour.get(child) === GREY) {
        looping.add(child);
      } else if (colour.get(child) === WHITE) {
        colour.set(child, GREY);
        stack.push({ node: child, children: childrenOf(child) });
      }
    }
  }
  return [...looping].sort();
}
